import { useEffect, useState } from "react";

import { Outlet, useLocation, useNavigate } from "react-router-dom";

import LNB from "@/components/LNB";
import { GetCurrentUser } from "@/services/Setting";
import useUserStore from "@/stores/User";
import useBidStore from "@/stores/Bid";
import { queryClient } from "@/libs/react-query/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { saveReturnUrl } from "@/utils/returnUrlUtils";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function AuthGuard({}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLNBOpen, setIsLNBOpen] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const setUser = useUserStore((state) => state.setUser);

  const handleLoginRequired = () => {
    setIsLoading(false);
    setShowLoginDialog(true);
  };

  const handleConfirmLogin = () => {
    setShowLoginDialog(false);
    saveReturnUrl(location.pathname + location.search);
    navigate("/login", { replace: true });
  };

  const checkAuthentication = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);

    try {
      const result = await GetCurrentUser();
      if (result.data.success && result.data.pods[0]?.content) {
        setUser(result.data.pods[0].content);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        handleLoginRequired();
      }
    } catch (error) {
      console.log("인증 확인 실패:", error);
      setIsAuthenticated(false);
      handleLoginRequired();
    } finally {
      if (showLoading && !showLoginDialog) setIsLoading(false);
    }
  };

  // 다른 탭에서 로그아웃 이벤트 감지
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === "logout_event") {
      queryClient.clear();
      useUserStore.getState().reset();
      useBidStore.getState().reset();
      setIsAuthenticated(false);
      handleLoginRequired();
    }
  };

  // 페이지 가시성 변경 시 세션 재검증
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && isAuthenticated) {
      checkAuthentication(false);
    }
  };

  useEffect(() => {
    checkAuthentication();
    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/") {
      navigate("/bid/domestic/list");
    }
  }, [isAuthenticated, location.pathname]);

  const handleToggleLNB = () => {
    setIsLNBOpen((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">인증 확인 중...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <AlertDialog open={showLoginDialog}>
          {/* x 버튼 없애기 */}
          <AlertDialogContent className="[&_.absolute.right-0.top-0]:!hidden">
            <AlertDialogHeader>
              <AlertDialogTitle>로그인이 필요합니다</AlertDialogTitle>
              <AlertDialogDescription className="mt-3">
                이 페이지에 접근하려면 로그인이 필요합니다.
                <br />
                로그인 페이지로 이동하시겠습니까?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={handleConfirmLogin}
                className="hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
              >
                로그인하러 가기
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-row min-h-screen h-screen overflow-hidden">
      <LNB isOpen={isLNBOpen} onToggle={handleToggleLNB} />
      <Outlet />
    </div>
  );
}
